import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { RequireAuth } from "@/components/RequireAuth";
import { api } from "@/lib/apiClient";
import {
  clearGuestTabletSession,
  fetchGuestTabletSessionStaff,
  fetchGuestTabletWorkstations,
  startGuestTabletDemo,
  updateGuestTabletSession,
} from "@/lib/guestTabletApi";
import { readGuestTabletWorkstationCode } from "@/lib/guestTabletWorkstationStorage";
import { formatIsoDateShort } from "@/lib/bookingDates";
import type { Booking } from "@/types/camping";
import type { GuestTabletSessionDisplay, GuestTabletWorkstation } from "@/types/guestTablet";

const CHECK_IN_STATUSES = [
  { value: "idle", label: "Leerlauf" },
  { value: "booking_found", label: "Buchung gefunden" },
  { value: "stay_preparing", label: "Aufenthalt wird vorbereitet" },
  { value: "registration_checking", label: "Meldedaten werden geprüft" },
  { value: "payment_checking", label: "Zahlung wird geprüft" },
  { value: "access_preparing", label: "Gästekarte / Zugang wird vorbereitet" },
  { value: "wlan_providing", label: "WLAN-Informationen werden bereitgestellt" },
  { value: "check_in_complete", label: "Check-in abgeschlossen" },
];

export function GuestTabletControlPage() {
  const { workstationCode } = useParams<{ workstationCode?: string }>();
  const [workstations, setWorkstations] = useState<GuestTabletWorkstation[]>([]);
  const [selectedCode, setSelectedCode] = useState(
    () => workstationCode ?? readGuestTabletWorkstationCode() ?? "reception-1",
  );
  const [session, setSession] = useState<GuestTabletSessionDisplay | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingId, setBookingId] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [ws, b] = await Promise.all([
      fetchGuestTabletWorkstations(),
      api.get<Booking[]>("/api/v1/bookings/arrivals"),
    ]);
    setWorkstations(ws);
    setBookings(b);
    const display = await fetchGuestTabletSessionStaff(selectedCode);
    setSession(display);
  }, [selectedCode]);

  useEffect(() => {
    void load().catch(() => setMessage("Daten konnten nicht geladen werden."));
  }, [load]);

  const run = async (fn: () => Promise<GuestTabletSessionDisplay>) => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await fn();
      setSession(result);
      setMessage("Gespeichert.");
    } catch {
      setMessage("Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <RequireAuth>
      <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold">Gäste-Tablet Steuerung</h1>
          <Link
            to={`/guest-tablet/${selectedCode}`}
            target="_blank"
            rel="noreferrer"
            className="text-accent underline"
          >
            Tablet öffnen
          </Link>
        </div>

        <label className="block">
          <span className="text-sm text-label-secondary">Arbeitsplatz</span>
          <select
            className="mt-1 w-full rounded-lg border border-separator bg-card px-3 py-2"
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
          >
            {workstations.map((ws) => (
              <option key={ws.id} value={ws.code}>
                {ws.label} ({ws.code})
              </option>
            ))}
          </select>
        </label>

        {session && (
          <p className="text-sm text-label-secondary">
            Status: <strong>{session.check_in_status}</strong>
            {session.is_demo_mode && " · Demo"}
            {session.welcome && ` · ${session.welcome.guest_display_name}`}
          </p>
        )}

        <section className="rounded-xl border border-separator bg-card p-4 space-y-3">
          <h2 className="font-semibold">Buchung zuweisen</h2>
          <select
            className="w-full rounded-lg border border-separator bg-app px-3 py-2"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
          >
            <option value="">— Buchung wählen —</option>
            {bookings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.booking_number} · {formatIsoDateShort(b.arrival_date)}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!bookingId || busy}
            className="rounded-lg bg-accent px-4 py-2 text-white disabled:opacity-50"
            onClick={() =>
              void run(() => updateGuestTabletSession(selectedCode, { booking_id: bookingId }))
            }
          >
            Auf Tablet anzeigen
          </button>
        </section>

        <section className="rounded-xl border border-separator bg-card p-4 space-y-3">
          <h2 className="font-semibold">Check-in-Status</h2>
          <div className="flex flex-wrap gap-2">
            {CHECK_IN_STATUSES.map((s) => (
              <button
                key={s.value}
                type="button"
                disabled={busy}
                className="rounded-lg border border-separator px-3 py-2 text-sm hover:bg-fill"
                onClick={() =>
                  void run(() =>
                    updateGuestTabletSession(selectedCode, { check_in_status: s.value }),
                  )
                }
              >
                {s.label}
              </button>
            ))}
          </div>
        </section>

        <section className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={busy}
            className="rounded-lg border border-separator px-4 py-2"
            onClick={() => void run(() => startGuestTabletDemo(selectedCode))}
          >
            Demo starten
          </button>
          <button
            type="button"
            disabled={busy}
            className="rounded-lg border border-red-300 px-4 py-2 text-red-700"
            onClick={() => void run(() => clearGuestTabletSession(selectedCode))}
          >
            Tablet leeren
          </button>
        </section>

        {message && <p className="text-sm text-label-secondary">{message}</p>}
      </div>
    </RequireAuth>
  );
}
